import { RunContextApp } from 'framework/RCApp';

// API Response Interfaces
export interface ApiColorItem {
  contrastPass: boolean;
  contrastRatio: number;
  displayName: string;
  hexValue: string;
  shade: string;
  usageCount: number;
  colorMappings?: Record<string, string[]>;
}

export interface ApiCategory {
  colors: ApiColorItem[];
  description: string;
  id: string;
  name: string;
}

export interface ColorPaletteApiResponse {
  channel: 'DIGITAL' | 'PAPER';
  categories: ApiCategory[];
}

// Impact API Response Interfaces
export interface ImpactTemplate {
  productType: string;    // e.g., "EMAIL.SF.MONTHLY_SUMMARY"
  product: string;        // e.g., "EMAIL"
  fuelType: string;       // e.g., "ELECTRIC"
  sections: string[];
}

export interface ColorImpactResponse {
  id: string;
  shade: string;
  totalTemplates: number;
  totalSections: number;
  affectedTemplates: ImpactTemplate[];
}

// Save Color Response Interfaces
export interface SaveColorResponse {
  success: boolean;
  category: string;
  channel: string;
  config_level: string;
  standard_id: string;
  shade: string;
  newHexValue: string;
  versionId: string;
  fallback_used?: boolean;
  db_attempts?: number;
  push_attempts?: number;
  db_result?: {
    created_count: number;
    updated_count: number;
    results: Array<{
      action: string;
      config_key: string;
      level: string;
      project_id: string;
    }>;
  };
  push_result?: {
    ok: boolean;
    total: number;
    total_success: number;
    total_failed: number;
  };
  configs_updated?: Array<{
    config_key: string;
    config_type: string;
    had_existing_config: boolean;
    is_json_shade_value: boolean;
  }>;
}

// Request Interfaces
export interface GetColorPaletteParams {
  channel: 'DIGITAL' | 'PAPER';
  backgroundHEX: string;
  projectId?: string;
}

export interface GetColorImpactParams {
  id: string;           // Category ID (e.g., "primary", "data_colors")
  shade: string;        // Shade (e.g., "200", "graph_primary")
  projectId?: string;
  channel: 'DIGITAL' | 'PAPER';
  level: 'PILOT' | 'CLUSTER';
}

export interface SaveColorParams {
  project_id: string;
  channel: 'DIGITAL' | 'PAPER';
  id: string;           // Category ID (e.g., "data_colors", "primary")
  shade: string;        // Shade name (e.g., "graph_primary", "200", "500")
  newHexValue: string;
  config_level: string;
  source_name: string;
}

export const ColorPaletteAPI = {

  getColorPalette: async (
    rc: RunContextApp, 
    params: GetColorPaletteParams
  ): Promise<{ success: boolean; data?: ColorPaletteApiResponse; error?: string }> => {
    try {
      const response = await rc.bffClient.getColorPalette(params);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: typeof response.error === 'string' 
            ? response.error 
            : (response.error as any)?.message || 'Failed to fetch color palette'
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data as ColorPaletteApiResponse
        };
      }

      return {
        success: true,
        data: response as any
      };
    } catch (error) {
      rc.error(rc.getName(ColorPaletteAPI), 
        `Error >> GET request to : /v1/cx/color-palette\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  getColorImpact: async (
    rc: RunContextApp, 
    params: GetColorImpactParams
  ): Promise<{ success: boolean; data?: ColorImpactResponse; error?: string }> => {
    try {
      const response = await rc.bffClient.getColorImpact(params);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: typeof response.error === 'string' 
            ? response.error 
            : (response.error as any)?.message || 'Failed to fetch color impact'
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data as ColorImpactResponse
        };
      }

      return {
        success: true,
        data: response as any
      };
    } catch (error) {
      rc.error(rc.getName(ColorPaletteAPI), 
        `Error >> GET request to : /v1/cx/color-palette/${params.id}/${params.shade}/impact\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  saveColor: async (
    rc: RunContextApp, 
    params: SaveColorParams
  ): Promise<{ success: boolean; data?: SaveColorResponse; error?: string }> => {
    try {
      const response = await rc.bffClient.saveColorPalette(params);

      if ('error' in response && response.error) {
        return {
          success: false,
          error: typeof response.error === 'string' 
            ? response.error 
            : (response.error as any)?.message || 'Failed to save color'
        };
      }

      if ('data' in response && response.data) {
        return {
          success: true,
          data: response.data as SaveColorResponse
        };
      }

      return {
        success: true,
        data: response as any
      };
    } catch (error) {
      rc.error(rc.getName(ColorPaletteAPI), 
        `Error >> POST request to : /v1/cx/color-palette/save\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },
};

export default ColorPaletteAPI;
